/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { TranslationDropDownLabel } from 'src/translation/components/TranslationDropDownLabel';

const linkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: { fontSize: '12px' }
});

const messages = defineMessages({
    folderTitle: { id: 'translation.translationFileLocationDropDown.folderTitle', defaultMessage: 'Folder (optional)' },
    blobContainerTitle: { id: 'translation.translationFileLocationDropDown.blobContainerTitle', defaultMessage: 'Blob container' },
    storageAccountTitle: { id: 'translation.translationFileLocationDropDown.storageAccountTitle', defaultMessage: 'Storage account' },
    containersEmptyPlaceholder: {
        id: 'translation.translationFileLocationDropDown.containersEmptyPlaceholder',
        defaultMessage: 'Select a blob container'
    },
    blobContainerDescription: {
        id: 'translation.translationFileLocationDropDown.blobContainerDescription',
        defaultMessage:
            'This is the container within your Azure storage account {storageAccount} that contains the documents you want to translate'
    },
    createNewFolder: {
        id: 'translation.translationFileLocationDropDown.createNewFolder',
        defaultMessage: `Create new folder '{folderName}'`
    },
    clearFolderSelection: {
        id: 'translation.translationFileLocationDropDown.clearFolderSelection',
        defaultMessage: `Clear folder selection`
    },
    folderEmptyPlaceholder: {
        id: 'translation.translationFileLocationDropDown.folderEmptyPlaceholder',
        defaultMessage: 'Select a Blob container to view folders'
    },
    storageAccountDescription: {
        id: 'translation.translationFileLocationDropDown.storageAccountDescription',
        defaultMessage: 'This is the storage account you have selected during initial configuration'
    }
});

type TranslationFileLocationDropDownsProps = {
    storageAccountName: string;
    isHorizontalDropDowns?: boolean;

    // Props needed for rendering storage account dropdown
    isStorageAccountsLoading?: boolean;
    showStorageAccountDropDown?: boolean;
    storageAccounts?: AzureStorageAccountStore[];

    // Props needed for rendering containers dropdown
    isStorageContainersLoading: boolean;
    isSelectContainerDisabled?: boolean;
    selectedDestinationContainer: string;
    storageContainers: AzureBlobContainerStore[];
    onChangeStorageContainer: (option: string) => void;

    // Props needed for rendering folders dropdown
    selectedFolder?: string;
    blobFiles?: AzureBlobStore[];
    isBlobFilesLoading?: boolean;
    isSelectFolderDisabled?: boolean;
    showFoldersDropDown?: boolean;
    blobFilesTracker?: ProgressTrackerStore;
    onChangeDestinationFolder?: (folder: string) => void;
};

export const TranslationFileLocationDropDowns = (props: TranslationFileLocationDropDownsProps) => {
    const {
        selectedFolder,
        blobFiles = [],
        blobFilesTracker,
        storageContainers,
        storageAccountName,
        isBlobFilesLoading,
        storageAccounts = [],
        onChangeStorageContainer,
        isStorageAccountsLoading,
        onChangeDestinationFolder,
        isStorageContainersLoading,
        showFoldersDropDown = false,
        isHorizontalDropDowns = true,
        selectedDestinationContainer,
        isSelectFolderDisabled = true,
        isSelectContainerDisabled = false,
        showStorageAccountDropDown = false
    } = props;

    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();

    const containersOptions: IDropdownOption[] = storageContainers.map(c => ({ key: c.name, text: c.name }));
    const storageAccountsOptions: IDropdownOption[] = storageAccounts.map(c => ({ key: c.name, text: c.name }));

    const foldersOptions = blobFiles
        .filter(f => f.name.includes('/'))
        .map(store => store.name.substring(0, store.name.indexOf('/')))
        .filter((v, i, a) => a.indexOf(v) === i)
        .map(name => ({
            key: name,
            text: name
        }));

    return (
        <Stack horizontal={isXLargeOrGreater && isHorizontalDropDowns} tokens={{ childrenGap: 10 }} styles={{ root: { flexWrap: 'wrap' } }}>
            {showStorageAccountDropDown && (
                <LuisDropdown
                    options={storageAccountsOptions}
                    selectedKey={storageAccountName}
                    styles={{ dropdown: { minWidth: '230px' } }}
                    aria-labelledby="azureStorageAccountDropdown"
                    data-automation-id="azureStorageAccountDropdown"
                    isLoading={isStorageAccountsLoading}
                    disabled
                    placeholder={storageAccountName}
                    label={localize(messages.storageAccountTitle)}
                    onRenderLabel={() => (
                        <TranslationDropDownLabel
                            id="azureStorageAccountDropdown"
                            label={localize(messages.storageAccountTitle)}
                            descriptionIconProps={{
                                content: localize(messages.storageAccountDescription)
                            }}
                        />
                    )}
                />
            )}

            <LuisDropdown
                options={containersOptions}
                selectedKey={selectedDestinationContainer}
                disabled={isSelectContainerDisabled}
                onChange={(_, option) => onChangeStorageContainer(option.key as string)}
                data-automation-id="blobContainerDropdown"
                aria-labelledby="blobContainerDropdown"
                label={localize(messages.blobContainerTitle)}
                styles={{ dropdown: { minWidth: '280px' } }}
                calloutProps={{ styles: { calloutMain: { maxHeight: '280px !important' } } }}
                isLoading={isStorageContainersLoading}
                placeholder={localize(messages.containersEmptyPlaceholder)}
                onRenderLabel={() => (
                    <TranslationDropDownLabel
                        id="blobContainerDropdown"
                        label={localize(messages.blobContainerTitle)}
                        descriptionIconProps={{
                            content: localize(messages.blobContainerDescription, { storageAccount: storageAccountName })
                        }}
                    />
                )}
            />

            {showFoldersDropDown && (
                <Stack verticalAlign="start" tokens={{ childrenGap: 5 }}>
                    <LuisDropdown
                        options={foldersOptions}
                        selectedKey={selectedFolder}
                        styles={{ dropdown: { minWidth: '300px' } }}
                        onChange={(_, option) => onChangeDestinationFolder(option.key as string)}
                        calloutProps={{ styles: { calloutMain: { maxHeight: '300px !important' } } }}
                        isLoading={blobFilesTracker.isRunning || isBlobFilesLoading}
                        label={localize(messages.folderTitle)}
                        disabled={isSelectFolderDisabled}
                        placeholder={localize(messages.folderEmptyPlaceholder)}
                        onRenderNoMatchItem={query => ({
                            data: query,
                            key: query,
                            text: localize(messages.createNewFolder, { folderName: query })
                        })}
                    />
                    <Link as="button" onClick={() => onChangeDestinationFolder(null)} styles={linkStyles}>
                        {localize(messages.clearFolderSelection)}
                    </Link>
                </Stack>
            )}
        </Stack>
    );
};
